<script lang="ts">
  import type { StudioIncludes } from '@bruce/api/api/studio/response'
  import StudioCard from './StudioCard.svelte'
  import { browser } from '$app/environment'
  import { STRINGS } from '$lib/constants/strings'
  import { page } from '$app/stores'
  import { Button, type Language } from '@bruce/components'
  import type { TierName } from '@bruce/api/common/tier'
  import { utils } from '$lib/utils'

  export let imageUrl: string
  export let studios: StudioIncludes<{ params: object }>[]
  export let arrowOffset: number | undefined = undefined
  export let title: string
  export let tierName: TierName | undefined = undefined
  export let button = true

  let grid: HTMLUListElement | undefined = undefined
  let container: HTMLDivElement | undefined = undefined

  $: lang = $page.params.lang as Language
  $: localeRoute = utils.getRoute(lang)
  $: centerListWhenOverflow(grid, container)

  function centerListWhenOverflow(
    grid: HTMLUListElement | undefined,
    container: HTMLDivElement | undefined,
  ) {
    if (container && grid && grid.offsetWidth < grid.scrollWidth && browser) {
      container.scrollLeft = grid.scrollWidth / 2 - container.offsetWidth / 2
    }
  }
</script>

<div class="featured-studios-container">
  {#if arrowOffset !== undefined}
    <div
      class="featured-studios-arrow"
      style={`transform: translateX(${arrowOffset}px)`}
    ></div>
  {/if}
  <div class="featured-studios">
    <h3>
      {title}
    </h3>
    <div class="container" bind:this={container}>
      <div class="shadow-left"></div>
      <div class="shadow-right"></div>
      <ul bind:this={grid}>
        {#each studios as studio (studio.id)}
          <li>
            <StudioCard {imageUrl} {studio} size="sm" vicinity />
          </li>
        {/each}
      </ul>
    </div>
    {#if button}
      <Button
        href={localeRoute(`/studios?tier=${tierName?.toLowerCase()}`)}
        rounded
        variant="outlined"
      >
        {STRINGS[lang].MEMBERSHIP_CARDS_EXPLORE_ALL}
        {tierName}
        {STRINGS[lang].MEMBERSHIP_CARDS_STUDIOS}
      </Button>
    {/if}
  </div>
</div>

<style lang="scss">
  @use '@bruce/assets/css/breakpoints';

  .featured-studios-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin-top: 2em;

    .featured-studios-arrow,
    .featured-studios {
      background-color: var(
        --membership-featured-studios-background-color,
        var(--charcoal-black)
      );
      color: #fff;
      --button-text-color: #fff;
    }

    .featured-studios-arrow {
      position: absolute;
      top: -2em;
      width: 6em;
      height: 3em;
      clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
      transition: transform 300ms cubic-bezier(0.25, 0.8, 0.5, 1);

      @media screen and (max-width: breakpoints.$breakpoint-md) {
        transform: unset !important;
      }
    }

    .featured-studios {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2em;
      width: 100%;
      padding: 2em 0;
      z-index: 2;
      overflow-x: hidden;

      h3 {
        margin: 0;
      }

      .container {
        --container-offset: 8em;
        position: relative;
        width: calc(100% + var(--container-offset) * 2);

        @media screen and (max-width: breakpoints.$breakpoint-sm) {
          --container-offset: 0;
          position: unset;
          width: 100%;
          overflow-x: auto;
          overscroll-behavior-x: contain;

          &::-webkit-scrollbar {
            display: none;
          }
        }

        .shadow-left,
        .shadow-right {
          position: absolute;
          top: 0;
          height: 100%;
          pointer-events: none;
          width: 100px;
          z-index: 3;
        }

        .shadow-left {
          left: var(--container-offset);
          background: linear-gradient(
            to right,
            rgb(var(--featured-studio-grid-shadow, var(--charcoal-black-rgb)))
              20%,
            rgba(
                var(--featured-studio-grid-shadow, var(--charcoal-black-rgb)),
                0
              )
              80%
          );
        }

        .shadow-right {
          right: var(--container-offset);
          background: linear-gradient(
            to left,
            rgb(var(--featured-studio-grid-shadow, var(--charcoal-black-rgb)))
              20%,
            rgba(
                var(--featured-studio-grid-shadow, var(--charcoal-black-rgb)),
                0
              )
              80%
          );
        }

        ul {
          display: grid;
          grid-template-columns: repeat(14, minmax(85px, 1fr));
          gap: 0.75em;
          list-style-type: none;
          margin: 0;
          padding: 0;

          li {
            grid-column: span 2;
          }

          li:nth-child(1),
          li:nth-child(14) {
            grid-column: 2 / span 2;
          }
        }
      }
    }
  }
</style>
